import React, { useState, useEffect, useRef, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import StockChart from '../../components/charts/StockChart/StockChart';
import TechnicalIndicators from '../../components/charts/TechnicalIndicators/TechnicalIndicators';
import './Analysis.css';
import apiClient from '../../utils/api';

const Analysis = () => {
  const [symbol, setSymbol] = useState('AAPL');
  const [analysisType, setAnalysisType] = useState('technical');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [usdInr, setUsdInr] = useState(83.0); // default fallback
  const debounceRef = useRef(null);
  const [showClickReset, setShowClickReset] = useState(false);
  const [priceClick, setPriceClick] = useState(null);
  const [maClick, setMaClick] = useState(null);
  const [rsiClick, setRsiClick] = useState(null);
  const [macdClick, setMacdClick] = useState(null);
  const [volumeClick, setVolumeClick] = useState(null);
  // Modal state for "Read more"
  const [readMore, setReadMore] = useState({ open: false, title: '', content: '' });
  // State for analysis read more popup
  const [analysisReadMore, setAnalysisReadMore] = useState({ open: false, title: '', content: '' });
  // State for LLM price chart analysis
  const [priceChartAnalysis, setPriceChartAnalysis] = useState({ loading: false, data: null, error: null });
  // Track the last analyzed point to detect new clicks
  const [lastAnalyzedPoint, setLastAnalyzedPoint] = useState(null);

  // Reusable AnalysisCard component with read more functionality
  const AnalysisCard = ({ title, content, height = 200 }) => {
    const contentRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setIsOverflowing(overflowing);
    }, [content, height]);

    return (
      <Card title={title} style={{ width: 300, height, marginBottom: 0 }}>
        <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
          <div ref={contentRef} style={{ height: '100%', overflow: 'hidden', color: '#6b7280', padding: 16 }}>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{content}</p>
          </div>
          {isOverflowing && (
            <>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 54,
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.9))',
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  pointerEvents: 'none',
                }}
              />
              <button
                onClick={() => setAnalysisReadMore({ open: true, title, content })}
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  fontSize: 12,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  color: '#111827',
                  cursor: 'pointer',
                }}
                aria-label={`Read more: ${title}`}
              >
                Read more
              </button>
            </>
          )}
        </div>
      </Card>
    );
  };

  // Local helper card that detects overflow and exposes a Read more affordance
  const PreviewCard = ({ title, content, height, style }) => {
    const contentRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      // Measure overflow
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setIsOverflowing(overflowing);
    }, [content, height, showClickReset]);

    return (
      <Card title={title} style={{ width: 300, borderRadius: 12, height, ...(style || {}) }}>
        <div
          ref={contentRef}
          style={{
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            color: '#6b7280',
            textAlign: 'left',
            padding: 16,
          }}
        >
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{content}</p>

          {isOverflowing && (
            <>
              {/* Fade-out gradient at bottom */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 54,
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.9))',
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  pointerEvents: 'none',
                }}
              />
              {/* Read more button */}
              <button
                onClick={() => setReadMore({ open: true, title, content })}
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  fontSize: 12,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  color: '#111827',
                  cursor: 'pointer',
                }}
                aria-label={`Read more: ${title}`}
              >
                Read more
              </button>
            </>
          )}
        </div>
      </Card>
    );
  };

  // Generic overflow wrapper for arbitrary JSX content inside Cards
  const OverflowWrapper = ({ title, height, children }) => {
    const contentRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      const overflowing = el.scrollHeight > el.clientHeight + 1;
      setIsOverflowing(overflowing);
    }, [children, height, showClickReset]);

    return (
      <div style={{ position: 'relative', height, overflow: 'hidden' }}>
        <div ref={contentRef} style={{ height: '100%', overflow: 'hidden' }}>
          {children}
        </div>
        {isOverflowing && (
          <>
            <div
              style={{
                position: 'absolute', left: 0, right: 0, bottom: 0, height: 54,
                background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.9))',
                borderBottomLeftRadius: 12, borderBottomRightRadius: 12, pointerEvents: 'none'
              }}
            />
            <button
              onClick={() => setReadMore({ open: true, title, content: contentRef.current ? contentRef.current.innerText : '' })}
              style={{
                position: 'absolute', right: 12, bottom: 12, fontSize: 12,
                padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb',
                background: '#fff', color: '#111827', cursor: 'pointer'
              }}
              aria-label={`Read more: ${title}`}
            >
              Read more
            </button>
          </>
        )}
      </div>
    );
  };

  // Small helpers for formatting INR compactly
  const compactNumber = (num) => {
    const n = Number(num);
    const abs = Math.abs(n);
    if (abs >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
    if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return Number.isFinite(n) ? n.toFixed(2) : 'N/A';
  };
  const fmtINR = (v) => (v == null ? 'N/A' : `₹${compactNumber(v)}`);

  // Handle chart clicks from child charts
  const handleChartClick = useCallback((payload) => {
    // payload: { type: 'price'|'rsi'|'macd'|'volume', symbol, event, time, logical, point }
    try {
      console.log('[Analysis] Chart click:', payload);
      // Show reset toggle UI and hide sidebar widgets until reset
      setShowClickReset(true);
      if (!payload) return;
      // From main StockChart (no chartGroup) -> price card
      if (payload.type === 'price' && !payload.chartGroup) {
        setPriceClick(payload);
      }
      // From TechnicalIndicators (chartGroup: 'indicators')
      if (payload.chartGroup === 'indicators') {
        if (payload.type === 'price') setMaClick(payload);
        if (payload.type === 'rsi') {
          setRsiClick(payload);
          // RSI clicks should also trigger price chart analysis since they're related
          setPriceClick(payload);
        }
        if (payload.type === 'macd') {
          setMacdClick(payload);
          // MACD clicks should also trigger price chart analysis since they're related
          setPriceClick(payload);
        }
        if (payload.type === 'volume') {
          setVolumeClick(payload);
          // Volume clicks should also trigger price chart analysis since they're related
          setPriceClick(payload);
        }
      }
    } catch (_) {}
  }, []);

  const handleAnalysis = async () => {
    if (!symbol) return;
    setIsAnalyzing(true);
    setError(null);
    setInfo(null);

    try {
      const res = await apiClient.getStockInfoBasic(symbol);
      setInfo(res);
    } catch (e) {
      setError(e.message || 'Failed to fetch stock info');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const computeRecommendation = () => {
    if (!info) return { text: 'No data', confidence: null };
    const price = info.current_price;
    const low = info.fifty_two_week_low;
    const high = info.fifty_two_week_high;
    let rec = 'HOLD';
    let confidence = 0.5;

    if (price != null && low != null && high != null && high > low) {
      const pos = (price - low) / (high - low);
      if (pos < 0.3) { rec = 'CONSIDER BUY'; confidence = 0.7; }
      else if (pos > 0.7) { rec = 'CONSIDER TAKE PROFIT'; confidence = 0.7; }
      else { rec = 'HOLD'; confidence = 0.55; }
    }

    // Adjust by PE if available
    if (typeof info.pe_ratio === 'number') {
      if (info.pe_ratio < 15) confidence = Math.min(0.9, confidence + 0.1);
      if (info.pe_ratio > 35) confidence = Math.max(0.3, confidence - 0.15);
    }

    return { text: rec, confidence };
  };

  // Derived insights
  const getPerformanceSummary = () => {
    if (!info) return null;
    const price = info.current_price;
    const low = info.fifty_two_week_low;
    const high = info.fifty_two_week_high;
    if (price != null && low != null && high != null && high > low) {
      const pos = (price - low) / (high - low);
      if (pos < 0.3) return 'Trading near its 52-week low; sentiment may be weak or it could be a value opportunity.';
      if (pos > 0.7) return 'Trading closer to its 52-week high; momentum is strong but upside may be limited.';
      return 'Trading mid-range within its 52-week band; balanced risk/reward.';
    }
    return 'Insufficient historical context to assess performance range.';
  };

  const getValuationSummary = () => {
    if (!info) return null;
    const pe = info.pe_ratio;
    if (typeof pe === 'number') {
      if (pe < 15) return 'Looks relatively inexpensive versus typical market multiples (P/E < 15).';
      if (pe > 35) return 'Looks expensive relative to earnings (P/E > 35).';
      return 'Valuation appears moderate (P/E in normal range).';
    }
    return 'Valuation data is limited; P/E not available.';
  };

  const getRisksList = () => {
    if (!info) return [];
    const risks = [];
    const price = info.current_price;
    const low = info.fifty_two_week_low;
    const high = info.fifty_two_week_high;
    const pe = info.pe_ratio;
    if (typeof pe === 'number' && pe > 35) risks.push('High valuation may compress if growth slows.');
    if (price != null && low != null && high != null && high > low) {
      const pos = (price - low) / (high - low);
      if (pos > 0.85) risks.push('Near 52-week highs; potential pullback risk.');
      if (pos < 0.15) risks.push('Near 52-week lows; potential value trap if fundamentals are deteriorating.');
    }
    if (!(typeof info.dividend_yield === 'number' && info.dividend_yield > 0)) risks.push('No or low dividend; returns rely on price appreciation.');
    if (risks.length === 0) risks.push('No major red flags from basic metrics; review detailed financials for deeper risks.');
    return risks;
  };

  const formatCurrency = (value, currency) => {
    if (value == null) return 'N/A';
    const compact = (num) => {
      const abs = Math.abs(num);
      if (abs >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
      if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
      if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
      return Number(num).toFixed(2);
    };
    if (!currency || currency === 'USD') {
      const inr = value * usdInr;
      return `₹${compact(inr)} (USD $${compact(Number(value))})`;
    }
    if (currency === 'INR') {
      return `₹${compact(Number(value))}`;
    }
    // Unknown currency: show raw in compact
    return `${currency} ${compact(Number(value))}`;
  };

  const formatRange = (low, high, currency) => {
    if (low == null && high == null) return 'N/A';
    const compact = (num) => {
      const abs = Math.abs(num);
      if (abs >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
      if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
      if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
      return Number(num).toFixed(2);
    };
    const fmtInr = (v) => v == null ? '—' : `₹${compact(currency === 'USD' ? v * usdInr : v)}`;
    const fmtUsd = (v) => v == null ? '—' : `$${compact(Number(v))}`;
    if (currency && currency !== 'USD') {
      // Non-USD, just show native
      return `${fmtInr(low)} – ${fmtInr(high)}`;
    }
    // Assume USD -> show both INR and USD consolidated
    return `${fmtInr(low)} – ${fmtInr(high)} (USD ${fmtUsd(low)} – ${fmtUsd(high)})`;
  };

  const normalizeDividendPct = (divYield) => {
    if (divYield == null || isNaN(divYield)) return null;
    // Sometimes APIs return 0.0055 (fraction) or 0.55 (already percent) or 55 (bad)
    if (divYield > 1 && divYield <= 100) return divYield; // already percent
    if (divYield > 100) return 100; // cap clearly bad values
    return divYield * 100; // convert fraction to percent
  };

  // Check if current clicked point is different from last analyzed point
  const isNewPoint = () => {
    if (!lastAnalyzedPoint || !priceClick) return true;
    return lastAnalyzedPoint.time !== priceClick.time || 
           Math.abs((lastAnalyzedPoint.price || 0) - (priceClick.price || priceClick.ohlc?.close || 0)) > 0.01;
  };

  // Function to call LLM analysis for price chart
  const handlePriceChartAnalysis = async () => {
    if (!priceClick || !priceClick.time || !priceClick.history) {
      setPriceChartAnalysis({ loading: false, data: null, error: 'No price chart data available' });
      return;
    }

    setPriceChartAnalysis({ loading: true, data: null, error: null });

    try {
      // Prepare the request data
      const requestData = {
        symbol: symbol,
        clicked_date: new Date(priceClick.time).toISOString().split('T')[0],
        clicked_price: priceClick.price || priceClick.ohlc?.close || 0,
        ohlc: {
          t: new Date(priceClick.time).toISOString().split('T')[0],
          o: priceClick.ohlc?.open || priceClick.ohlc?.o || 0,
          h: priceClick.ohlc?.high || priceClick.ohlc?.h || 0,
          l: priceClick.ohlc?.low || priceClick.ohlc?.l || 0,
          c: priceClick.ohlc?.close || priceClick.ohlc?.c || 0,
          v: priceClick.ohlc?.volume || priceClick.ohlc?.v || 0
        },
        history: priceClick.history.map(item => ({
          t: item.time || item.t || new Date().toISOString().split('T')[0],
          o: item.open || item.o || 0,
          h: item.high || item.h || 0,
          l: item.low || item.l || 0,
          c: item.close || item.c || 0,
          v: item.volume || item.v || 0
        })),
        client_type: 'groq'
      };

      const response = await apiClient.post('/api/price-chart/analyze', requestData);
      
      if (response.analysis) {
        setPriceChartAnalysis({ 
          loading: false, 
          data: response, 
          error: null 
        });
        // Update the last analyzed point
        setLastAnalyzedPoint({
          time: priceClick.time,
          price: priceClick.price || priceClick.ohlc?.close || 0
        });
      } else {
        setPriceChartAnalysis({ 
          loading: false, 
          data: null, 
          error: 'No analysis data received' 
        });
      }
    } catch (error) {
      console.error('Error calling price chart analysis:', error);
      setPriceChartAnalysis({ 
        loading: false, 
        data: null, 
        error: error.message || 'Failed to get analysis' 
      });
    }
  };

  // Fetch live USD->INR rate on mount
  useEffect(() => {
    let active = true;
    const fetchFx = async () => {
      try {
        // Free, no-key endpoint
        const resp = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=INR');
        const data = await resp.json();
        const rate = data?.rates?.INR;
        if (active && typeof rate === 'number') setUsdInr(rate);
      } catch (_) {
        // keep fallback
      }
    };
    fetchFx();
    return () => { active = false; };
  }, []);

  // Auto-fetch on symbol change (debounced)
  useEffect(() => {
    if (!symbol) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleAnalysis();
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Reset inline analysis and restore full-width charts on symbol change
  useEffect(() => {
    setShowClickReset(false);
    setPriceClick(null);
    setMaClick(null);
    setRsiClick(null);
    setMacdClick(null);
    setVolumeClick(null);
    // Clear LLM analysis
    setPriceChartAnalysis({ loading: false, data: null, error: null });
    // Clear last analyzed point
    setLastAnalyzedPoint(null);
  }, [symbol]);

  return (
    <div className="analysis">
      <div className="analysis-header">
        <h1 className="analysis-title">Stock Analysis</h1>
        <p className="analysis-subtitle">
          Comprehensive technical and fundamental analysis
        </p>
      </div>

      <div className="analysis-controls">
        <Card className="controls-card">
          <div className="controls-grid">
            <div className="control-group">
              <label className="control-label">Stock Symbol</label>
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                className="control-input"
                placeholder="e.g., AAPL"
              />
            </div>

            <div className="control-group">
              <label className="control-label">Analysis Type</label>
              <select
                value={analysisType}
                onChange={(e) => setAnalysisType(e.target.value)}
                className="control-select"
              >
                <option value="technical">Technical Analysis</option>
                <option value="fundamental">Fundamental Analysis</option>
                <option value="comprehensive">Comprehensive Analysis</option>
              </select>
            </div>

            <div className="control-group">
              <label className="control-label">&nbsp;</label>
              <Button
                onClick={handleAnalysis}
                loading={isAnalyzing}
                variant="primary"
                size="large"
                className="analyze-btn"
              >
                {isAnalyzing ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {showClickReset && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div className="click-reset-bar" style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '4px 6px',
            marginTop: 0,
            marginBottom: 12,
            marginRight: 12,
            background: '#f5f5f7',
            borderRadius: 8, border: '1px solid #e5e7eb',
            width: 'fit-content'
          }}>
            <button
              onClick={() => { 
                setShowClickReset(false); 
                setPriceClick(null); 
                setMaClick(null); 
                setRsiClick(null); 
                setMacdClick(null); 
                setVolumeClick(null);
                // Clear LLM analysis
                setPriceChartAnalysis({ loading: false, data: null, error: null });
                // Clear last analyzed point
                setLastAnalyzedPoint(null);
                // Clear markers from all charts
                window.dispatchEvent(new CustomEvent('clearChartMarkers'));
              }}
              style={{
                fontSize: 14,
                padding: '4px 12px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: '#111827',
                cursor: 'pointer'
              }}
              aria-label="Reset view"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      <div className="analysis-content">
        <div className="analysis-main">
          <Card title="Price Chart" className="chart-card">
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0, width: showClickReset ? 'calc(100% - 312px)' : '100%' }}>
                <StockChart symbol={symbol} onChartClick={handleChartClick} showMaNote={true} />
              </div>
              {showClickReset && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* LLM Analysis Section */}
                  <Card title="Analysis for Price chart" style={{ width: 300, marginBottom: 0 }}>
                    <div style={{ padding: 16 }}>
                      {priceChartAnalysis.loading ? (
                        <div style={{ textAlign: 'center', color: '#6b7280' }}>
                          <div style={{ marginBottom: 8 }}>🤖 Analyzing with AI...</div>
                          <div style={{ fontSize: 12 }}>This may take a few seconds</div>
                        </div>
                      ) : priceChartAnalysis.error ? (
                        <div style={{ color: '#ef4444', fontSize: 14 }}>
                          ❌ {priceChartAnalysis.error}
                        </div>
                      ) : priceChartAnalysis.data ? (
                        <div>
                          <div style={{ marginBottom: 12 }}>
                            {/* Show indicator if this is a new point */}
                            {isNewPoint() && (
                              <div style={{ 
                                fontSize: 11, 
                                color: '#059669', 
                                backgroundColor: '#d1fae5', 
                                padding: '4px 8px', 
                                borderRadius: 4, 
                                marginBottom: 8,
                                textAlign: 'center'
                              }}>
                                ✨ New point clicked - analyze with AI for fresh insights
                              </div>
                            )}

                            <div style={{ 
                              whiteSpace: 'pre-wrap', 
                              fontSize: 13, 
                              lineHeight: 1.5,
                              maxHeight: 200,
                              overflow: 'hidden',
                              position: 'relative'
                            }}>
                              {priceChartAnalysis.data.analysis}
                            </div>
                          </div>
                          
                          {/* Show Analyze with AI button if this is a new point */}
                          {isNewPoint() && (
                            <div style={{ marginTop: 12, marginBottom: 8 }}>
                              <button
                                onClick={handlePriceChartAnalysis}
                                style={{
                                  fontSize: 12,
                                  padding: '8px 16px',
                                  borderRadius: 6,
                                  border: '1px solid #e5e7eb',
                                  background: '#3b82f6',
                                  color: '#fff',
                                  cursor: 'pointer',
                                  width: '100%',
                                  marginBottom: 8
                                }}
                              >
                                Analyze New Point with AI
                              </button>
                            </div>
                          )}
                          
                          <button
                            onClick={() => setAnalysisReadMore({ 
                              open: true, 
                              title: 'AI Price Chart Analysis', 
                              content: priceChartAnalysis.data.analysis 
                            })}
                            style={{
                              fontSize: 12,
                              padding: '6px 10px',
                              borderRadius: 6,
                              border: '1px solid #e5e7eb',
                              background: '#fff',
                              color: '#111827',
                              cursor: 'pointer',
                              width: '100%'
                            }}
                          >
                            Read Full Analysis
                          </button>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', color: '#6b7280' }}>
                          <div style={{ marginBottom: 8 }}>🤖 Get AI Analysis</div>
                          <div style={{ fontSize: 12, marginBottom: 12 }}>
                            Click the button below to get AI-powered technical analysis
                          </div>
                          <button
                            onClick={handlePriceChartAnalysis}
                            style={{
                              fontSize: 12,
                              padding: '8px 16px',
                              borderRadius: 6,
                              border: '1px solid #e5e7eb',
                              background: '#3b82f6',
                              color: '#fff',
                              cursor: 'pointer',
                              width: '100%'
                            }}
                          >
                            Click to Analyze
                          </button>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              )}
            </div>
          </Card>

          <Card title="Technical Indicators" className="indicators-card">
            <TechnicalIndicators 
              symbol={symbol} 
              onChartClick={handleChartClick}
              analysis={{ price: maClick, rsi: rsiClick, macd: macdClick, volume: volumeClick }}
              showClickReset={showClickReset}
              onReadMore={setAnalysisReadMore}
            />
          </Card>
        </div>

        {!showClickReset && (
          <div className="analysis-sidebar">
            <Card title="Recommendations" className="recommendations-card">
              <OverflowWrapper title="Recommendations" height={250}>
                {!info && !error && (
                  <div className="recommendations-placeholder"><p>{isAnalyzing ? 'Computing recommendation...' : 'Recommendations will appear here...'}</p></div>
                )}
                {info && (
                  <div className="recommendations-content">
                    {(() => {
                      const r = computeRecommendation();
                      const verdict = r.text === 'CONSIDER BUY' ? 'Worth considering for investment.' : r.text === 'CONSIDER TAKE PROFIT' ? 'May be prudent to reduce exposure.' : 'Hold; neither strongly undervalued nor overheated.';
                      const suggestions = [];
                      if (r.text === 'CONSIDER BUY') suggestions.push('Use staggered entries (SIP/DCA) to manage timing risk.');
                      if (r.text === 'CONSIDER TAKE PROFIT') suggestions.push('Trail stop-loss or book partial profits to lock gains.');
                      suggestions.push('Set alerts near key support/resistance from the chart.');
                      if (!(typeof info.dividend_yield === 'number' && info.dividend_yield > 0)) suggestions.push('If income is a goal, note the low/no dividend.');
                      return (
                        <div>
                          <p><strong>Verdict:</strong> {verdict}</p>
                          <p><strong>System signal:</strong> {r.text}{r.confidence != null ? ` (${(r.confidence * 100).toFixed(0)}% confidence)` : ''}</p>
                          <div style={{ marginTop: 8 }}>
                            <strong>Suggestions:</strong>
                            <ul style={{ paddingLeft: 18, margin: 0 }}>
                              {suggestions.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {error && (
                  <div className="recommendations-error"><p style={{ color: '#ef4444' }}>Unable to load recommendations.</p></div>
                )}
              </OverflowWrapper>
            </Card>

            <Card title="Analysis Results" className="results-card">
              <OverflowWrapper title="Analysis Results" height={250}>
                {error && (
                  <div className="results-error"><p style={{ color: '#ef4444' }}>Error: {error}</p></div>
                )}
                {!error && !info && (
                  <div className="results-placeholder">
                    <p>{isAnalyzing ? 'Loading analysis...' : 'Analysis results will appear here...'}</p>
                  </div>
                )}
                {!error && info && (
                  <div className="results-content">
                    <div style={{ marginBottom: 8}}>
                      <p><strong>Symbol:</strong> {info.symbol}</p>
                      {info.name && <p><strong>Name:</strong> {info.name}</p>}
                      {(info.sector || info.industry) && (
                        <p><strong>Sector/Industry:</strong> {info.sector || '—'} / {info.industry || '—'}</p>
                      )}
                    </div>

                    <div style={{ marginTop: 12, marginBottom: 12 }}>
                      <h4 style={{ margin: '8px 0' }}>What {info.name || info.symbol} does</h4>
                      {info.description ? (
                        <p style={{ whiteSpace: 'pre-wrap' }}>
                          {info.description.slice(0, 300)}{info.description.length > 300 ? '…' : ''}
                        </p>
                      ) : (
                        <p>Business description not available.</p>
                      )}
                      {info.website && (
                        <p><strong>Website:</strong> <a href={info.website} target="_blank" rel="noreferrer">{info.website}</a></p>
                      )}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <h4 style={{ margin: '8px 0' }}>🔹 Stock Performance</h4>
                      <p><strong>Current Price:</strong> {formatCurrency(info.current_price, info.currency)}</p>
                      {(info.fifty_two_week_low != null || info.fifty_two_week_high != null) && (
                        <p><strong>52-Week Range:</strong> {formatRange(info.fifty_two_week_low, info.fifty_two_week_high, info.currency)}</p>
                      )}
                      <p><strong>Interpretation:</strong> {getPerformanceSummary()}</p>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <h4 style={{ margin: '8px 0' }}>🔹 Valuation</h4>
                      {typeof info.pe_ratio === 'number' && (
                        <p>
                          <strong>P/E Ratio:</strong> {info.pe_ratio.toFixed(2)}
                          {` → ${info.pe_ratio < 15 ? 'Stock appears inexpensive relative to earnings.' : info.pe_ratio > 35 ? 'Stock appears expensive relative to earnings.' : 'Valuation appears moderate.'}`}
                        </p>
                      )}
                      {typeof info.dividend_yield === 'number' && (() => {
                        const pct = normalizeDividendPct(info.dividend_yield);
                        const cue = pct == null ? '' : pct >= 2 ? ' → Offers income support.' : pct >= 0.5 ? ' → Modest income for investors.' : ' → Minimal income.';
                        return (
                          <p><strong>Dividend Yield:</strong> {pct != null ? `${pct.toFixed(2)}%` : 'N/A'}{cue}</p>
                        );
                      })()}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <h4 style={{ margin: '8px 0' }}>🔹 Risks</h4>
                      <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {getRisksList().map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                        <li>Dependence on key product lines and global supply chain stability.</li>
                        <li>Competitive pressure from sector peers and emerging technologies.</li>
                      </ul>
                    </div>
                  </div>
                )}
              </OverflowWrapper>
            </Card>
          </div>
        )}
      </div>

      {analysisReadMore.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={analysisReadMore.title}
          onClick={() => setAnalysisReadMore({ open: false, title: '', content: '' })}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(760px, 92vw)',
              maxHeight: '82vh',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{
              padding: '14px 18px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0 }}>{analysisReadMore.title}</h3>
              <button
                onClick={() => setAnalysisReadMore({ open: false, title: '', content: '' })}
                style={{
                  fontSize: 12,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  cursor: 'pointer'
                }}
                aria-label="Close read more"
              >
                Close
              </button>
            </div>
            <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{analysisReadMore.content}</p>
            </div>
          </div>
        </div>
      )}

      {readMore.open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={readMore.title}
          onClick={() => setReadMore({ open: false, title: '', content: '' })}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(760px, 92vw)',
              maxHeight: '82vh',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{
              padding: '14px 18px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{ margin: 0 }}>{readMore.title}</h3>
              <button
                onClick={() => setReadMore({ open: false, title: '', content: '' })}
                style={{
                  fontSize: 12,
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  cursor: 'pointer'
                }}
                aria-label="Close read more"
              >
                Close
              </button>
            </div>
            <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{readMore.content}</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Analysis;